import Phaser from 'phaser';
import { COLORS, SHAPES, PHYSICS } from '../config/gameConfig';

/**
 * Shape types available in the game
 */
export enum ShapeType {
  SQUARE = 'SQUARE',
  CIRCLE = 'CIRCLE',
  TRIANGLE = 'TRIANGLE',
}

/**
 * Configuration for each shape's physics properties
 */
interface ShapeConfig {
  mass: number;
  friction: number;
  restitution: number;
}

/**
 * Physics configs mapped to shape types
 */
const SHAPE_CONFIGS: Record<ShapeType, ShapeConfig> = {
  [ShapeType.SQUARE]: {
    mass: SHAPES.SQUARE.MASS,
    friction: SHAPES.SQUARE.FRICTION,
    restitution: PHYSICS.RESTITUTION,
  },
  [ShapeType.CIRCLE]: {
    mass: SHAPES.CIRCLE.MASS,
    friction: SHAPES.CIRCLE.FRICTION,
    restitution: 0.2, // Circles bounce slightly more
  },
  [ShapeType.TRIANGLE]: {
    mass: SHAPES.TRIANGLE.MASS,
    friction: SHAPES.TRIANGLE.FRICTION,
    restitution: PHYSICS.RESTITUTION,
  },
};

/**
 * Color mapping for each shape type
 */
const SHAPE_COLORS: Record<ShapeType, number> = {
  [ShapeType.SQUARE]: COLORS.SQUARE,
  [ShapeType.CIRCLE]: COLORS.CIRCLE,
  [ShapeType.TRIANGLE]: COLORS.TRIANGLE,
};

/**
 * Paper aesthetic configuration
 */
const PAPER_CONFIG = {
  shadowOffsetX: 3,
  shadowOffsetY: 4,
  shadowAlpha: 0.12,
  shadowBlur: 2,
  highlightAlpha: 0.15,
  borderAlpha: 0.08,
  cornerRadius: 4, // For rounded corners on square
};

/**
 * Result of creating a shape - includes both physics body and graphics
 */
export interface ShapeObject {
  body: MatterJS.BodyType;
  graphics: Phaser.GameObjects.Graphics;
  shadow: Phaser.GameObjects.Graphics;
  type: ShapeType;
}

/**
 * ShapeFactory - Creates physics bodies and graphics for game shapes
 * Implements "Tactile Minimalism" paper aesthetic
 */
export class ShapeFactory {
  private scene: Phaser.Scene;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  /**
   * Create a shape at the given position
   */
  createShape(type: ShapeType, x: number, y: number): ShapeObject {
    const config = SHAPE_CONFIGS[type];
    const color = SHAPE_COLORS[type];

    let body: MatterJS.BodyType;
    let graphics: Phaser.GameObjects.Graphics;
    let shadow: Phaser.GameObjects.Graphics;

    switch (type) {
      case ShapeType.SQUARE:
        body = this.createSquareBody(x, y, config);
        shadow = this.createSquareShadow();
        graphics = this.createSquareGraphics(color);
        break;
      case ShapeType.CIRCLE:
        body = this.createCircleBody(x, y, config);
        shadow = this.createCircleShadow();
        graphics = this.createCircleGraphics(color);
        break;
      case ShapeType.TRIANGLE:
        body = this.createTriangleBody(x, y, config);
        shadow = this.createTriangleShadow();
        graphics = this.createTriangleGraphics(color);
        break;
    }

    return { body, graphics, shadow, type };
  }

  /**
   * Create a square physics body
   */
  private createSquareBody(x: number, y: number, config: ShapeConfig): MatterJS.BodyType {
    const size = SHAPES.SQUARE.SIZE;
    return this.scene.matter.add.rectangle(x, y, size, size, {
      friction: config.friction,
      frictionStatic: config.friction,
      restitution: config.restitution,
      label: 'square',
      slop: PHYSICS.SLOP,
    });
  }

  /**
   * Create a circle physics body
   */
  private createCircleBody(x: number, y: number, config: ShapeConfig): MatterJS.BodyType {
    const radius = SHAPES.CIRCLE.RADIUS;
    return this.scene.matter.add.circle(x, y, radius, {
      friction: config.friction,
      frictionStatic: config.friction,
      restitution: config.restitution,
      label: 'circle',
      slop: PHYSICS.SLOP,
    });
  }

  /**
   * Create a triangle physics body
   */
  private createTriangleBody(x: number, y: number, config: ShapeConfig): MatterJS.BodyType {
    const size = SHAPES.TRIANGLE.SIZE;
    const halfBase = size * 0.5;
    const height = size * 0.866; // Equilateral triangle height ratio

    // Vertices for equilateral triangle (centered)
    const vertices = [
      { x: 0, y: -height / 2 },
      { x: halfBase, y: height / 2 },
      { x: -halfBase, y: height / 2 },
    ];

    return this.scene.matter.add.fromVertices(x, y, [vertices], {
      friction: config.friction,
      frictionStatic: config.friction,
      restitution: config.restitution,
      label: 'triangle',
      slop: PHYSICS.SLOP,
    });
  }

  /**
   * Create shadow for square shape
   */
  private createSquareShadow(): Phaser.GameObjects.Graphics {
    const shadow = this.scene.add.graphics();
    const size = SHAPES.SQUARE.SIZE;
    const half = size / 2;

    shadow.fillStyle(0x000000, PAPER_CONFIG.shadowAlpha);
    shadow.fillRoundedRect(-half, -half, size, size, PAPER_CONFIG.cornerRadius);

    return shadow;
  }

  /**
   * Create shadow for circle shape
   */
  private createCircleShadow(): Phaser.GameObjects.Graphics {
    const shadow = this.scene.add.graphics();
    const radius = SHAPES.CIRCLE.RADIUS;

    shadow.fillStyle(0x000000, PAPER_CONFIG.shadowAlpha);
    shadow.fillCircle(0, 0, radius + 1);

    return shadow;
  }

  /**
   * Create shadow for triangle shape
   */
  private createTriangleShadow(): Phaser.GameObjects.Graphics {
    const shadow = this.scene.add.graphics();
    const size = SHAPES.TRIANGLE.SIZE;
    const halfBase = size * 0.5;
    const height = size * 0.866;

    shadow.fillStyle(0x000000, PAPER_CONFIG.shadowAlpha);
    shadow.beginPath();
    shadow.moveTo(0, -height / 2);
    shadow.lineTo(halfBase, height / 2);
    shadow.lineTo(-halfBase, height / 2);
    shadow.closePath();
    shadow.fillPath();

    return shadow;
  }

  /**
   * Create graphics for a square shape with paper aesthetic
   */
  private createSquareGraphics(color: number): Phaser.GameObjects.Graphics {
    const graphics = this.scene.add.graphics();
    const size = SHAPES.SQUARE.SIZE;
    const half = size / 2;

    // Main fill with rounded corners for paper feel
    graphics.fillStyle(color, 1);
    graphics.fillRoundedRect(-half, -half, size, size, PAPER_CONFIG.cornerRadius);

    // Inner highlight (top-left area for 3D effect)
    graphics.fillStyle(0xffffff, PAPER_CONFIG.highlightAlpha);
    graphics.fillRoundedRect(
      -half + 4,
      -half + 4,
      size / 3,
      size / 3,
      PAPER_CONFIG.cornerRadius
    );

    // Subtle border for tactile feel
    graphics.lineStyle(1.5, 0x000000, PAPER_CONFIG.borderAlpha);
    graphics.strokeRoundedRect(-half, -half, size, size, PAPER_CONFIG.cornerRadius);

    return graphics;
  }

  /**
   * Create graphics for a circle shape with paper aesthetic
   */
  private createCircleGraphics(color: number): Phaser.GameObjects.Graphics {
    const graphics = this.scene.add.graphics();
    const radius = SHAPES.CIRCLE.RADIUS;

    // Main fill
    graphics.fillStyle(color, 1);
    graphics.fillCircle(0, 0, radius);

    // Inner highlight (off-center for 3D effect)
    graphics.fillStyle(0xffffff, PAPER_CONFIG.highlightAlpha);
    graphics.fillCircle(-radius * 0.3, -radius * 0.3, radius * 0.35);

    // Subtle border
    graphics.lineStyle(1.5, 0x000000, PAPER_CONFIG.borderAlpha);
    graphics.strokeCircle(0, 0, radius);

    return graphics;
  }

  /**
   * Create graphics for a triangle shape with paper aesthetic
   */
  private createTriangleGraphics(color: number): Phaser.GameObjects.Graphics {
    const graphics = this.scene.add.graphics();
    const size = SHAPES.TRIANGLE.SIZE;
    const halfBase = size * 0.5;
    const height = size * 0.866;

    // Main fill
    graphics.fillStyle(color, 1);
    graphics.beginPath();
    graphics.moveTo(0, -height / 2);
    graphics.lineTo(halfBase, height / 2);
    graphics.lineTo(-halfBase, height / 2);
    graphics.closePath();
    graphics.fillPath();

    // Inner highlight (smaller triangle near top)
    graphics.fillStyle(0xffffff, PAPER_CONFIG.highlightAlpha);
    graphics.beginPath();
    graphics.moveTo(-5, -height / 4);
    graphics.lineTo(10, height / 6);
    graphics.lineTo(-15, height / 6);
    graphics.closePath();
    graphics.fillPath();

    // Subtle border
    graphics.lineStyle(1.5, 0x000000, PAPER_CONFIG.borderAlpha);
    graphics.beginPath();
    graphics.moveTo(0, -height / 2);
    graphics.lineTo(halfBase, height / 2);
    graphics.lineTo(-halfBase, height / 2);
    graphics.closePath();
    graphics.strokePath();

    return graphics;
  }

  /**
   * Get shadow offset configuration
   */
  static getShadowOffset(): { x: number; y: number } {
    return {
      x: PAPER_CONFIG.shadowOffsetX,
      y: PAPER_CONFIG.shadowOffsetY,
    };
  }
}
